import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, getSessionId } from "@/lib/api";
import { ThumbsUp, ThumbsDown, HelpCircle, CheckCircle2, MessageSquareHeart } from "lucide-react";
import { toast } from "sonner";
import { getParticipantId } from "@/lib/userInterviews";
import { useAuth } from "@/lib/auth";

/**
 * SurveySidePanel — slide-in 3-min survey that opens beside the dashboard
 * instead of navigating to /feedback. Lets users finish without losing
 * their place. The full /feedback page still exists for those who'd rather
 * go to a dedicated page (and Prolific completion-redirect logic still lives
 * there — this side panel is the "happy path" for organic real users).
 *
 * Captures the 4 fields that matter most for the analytics:
 *   - PMF (Sean Ellis): "How would you feel if you could no longer use it?"
 *   - WOM / NPS: 0-10 likelihood to recommend
 *   - Return frequency
 *   - Would-miss-most free text
 *
 * On submit, posts to /api/feedback (same endpoint /feedback uses) and
 * stamps `feedback_submitted_at` on the user record, which hides the
 * SurveyNudge permanently.
 */

const PMF_OPTIONS = [
    { value: "very", label: "Very disappointed", Icon: ThumbsUp, tone: "bg-stage_early/50 text-stage_early-dark", active: "bg-stage_early-dark text-white" },
    { value: "somewhat", label: "Somewhat disappointed", Icon: HelpCircle, tone: "bg-yellow-soft text-yellow-dark", active: "bg-yellow-dark text-white" },
    { value: "not", label: "Not disappointed", Icon: ThumbsDown, tone: "bg-coral-light text-coral-dark", active: "bg-coral-dark text-white" },
    { value: "na", label: "N/A", tone: "bg-cream-dark text-[#4C5551]", active: "bg-[#4C5551] text-white" },
];

const RETURN_FREQ = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "A few times a week" },
    { value: "monthly", label: "A few times a month" },
    { value: "rarely", label: "Only when something hard comes up" },
    { value: "never", label: "I don't see myself coming back" },
];

export default function SurveySidePanel({ open, onOpenChange }) {
    const { refresh } = useAuth();
    const sid = getSessionId();
    const participantId = getParticipantId();
    const [pmf, setPmf] = useState("");
    const [wom, setWom] = useState(null);
    const [returnFreq, setReturnFreq] = useState("");
    const [wouldMiss, setWouldMiss] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const hasAny = !!(pmf || wom !== null || returnFreq || wouldMiss.trim());

    const submit = async () => {
        if (!hasAny) {
            toast.error("Pick at least one answer so we have something to learn from.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/feedback", {
                session_id: sid,
                participant_id: participantId,
                pmf_disappointment: pmf,
                wom_likelihood: wom,
                return_frequency: returnFreq,
                would_miss_most: wouldMiss.trim(),
            });
            setDone(true);
            await refresh();
            toast.success("Thank you — your answers shape what we build next.");
        } catch (e) {
            toast.error("Couldn't submit just now. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-md md:max-w-lg bg-cream-light border-l border-cream-dark overflow-y-auto"
                data-testid="survey-side-panel"
            >
                {done ? (
                    <div className="flex flex-col items-center justify-center text-center py-12">
                        <div className="w-14 h-14 rounded-3xl bg-stage_early/50 flex items-center justify-center mb-4">
                            <CheckCircle2 strokeWidth={1.6} size={28} className="text-stage_early-dark" />
                        </div>
                        <h2 className="font-heading text-2xl text-[#12776F]">Thank you.</h2>
                        <p className="text-sm text-[#4C5551] mt-2 max-w-xs">
                            Your honest answers shape what we build next. Memory Lane is better because of you.
                        </p>
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            data-testid="survey-side-panel-close"
                            className="mt-6 inline-flex items-center justify-center gap-2 bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-5 py-2.5 text-sm font-medium"
                        >
                            Back to Memory Lane
                        </button>
                    </div>
                ) : (
                    <>
                        <SheetHeader>
                            <div className="flex items-center gap-2">
                                <MessageSquareHeart size={18} strokeWidth={1.7} className="text-teal-dark" />
                                <SheetTitle className="font-heading text-xl text-[#1F2624]">3 minutes, that&apos;s all.</SheetTitle>
                            </div>
                            <SheetDescription className="text-sm text-[#4C5551]">
                                Your honest answers tell us what to keep, what to fix, and what to build next.
                            </SheetDescription>
                        </SheetHeader>

                        <div className="mt-6 space-y-6 pb-4">
                            {/* PMF */}
                            <div>
                                <p className="text-sm font-medium text-[#1F2624]">
                                    How would you feel if you could no longer use Memory Lane?
                                </p>
                                <div className="grid grid-cols-1 gap-2 mt-3">
                                    {PMF_OPTIONS.map((o) => {
                                        const Icon = o.Icon;
                                        const isActive = pmf === o.value;
                                        return (
                                            <button
                                                key={o.value}
                                                type="button"
                                                onClick={() => setPmf(o.value)}
                                                data-testid={`side-pmf-${o.value}`}
                                                className={`flex items-center gap-2 px-3 py-2.5 rounded-2xl text-sm border transition-all ${isActive ? o.active + " border-transparent" : o.tone + " border-transparent hover:opacity-90"}`}
                                            >
                                                {Icon && <Icon size={16} strokeWidth={1.8} />}
                                                <span>{o.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* WOM */}
                            <div>
                                <p className="text-sm font-medium text-[#1F2624]">
                                    How likely are you to recommend Memory Lane to another caregiver?
                                </p>
                                <p className="text-xs text-[#4C5551] mt-0.5">0 = not likely, 10 = absolutely.</p>
                                <div className="grid grid-cols-11 gap-1 mt-3">
                                    {Array.from({ length: 11 }).map((_, n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setWom(n)}
                                            data-testid={`side-wom-${n}`}
                                            className={`h-9 rounded-lg text-sm font-semibold tabular-nums transition-all ${wom === n
                                                ? "bg-teal-dark text-white"
                                                : "bg-white border border-cream-dark text-[#4C5551] hover:bg-cream"
                                                }`}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Return frequency */}
                            <div>
                                <p className="text-sm font-medium text-[#1F2624]">
                                    How often do you see yourself coming back?
                                </p>
                                <div className="grid grid-cols-1 gap-1.5 mt-3">
                                    {RETURN_FREQ.map((r) => (
                                        <button
                                            key={r.value}
                                            type="button"
                                            onClick={() => setReturnFreq(r.value)}
                                            data-testid={`side-rf-${r.value}`}
                                            className={`text-left px-3 py-2 rounded-2xl text-sm border transition-all ${returnFreq === r.value
                                                ? "bg-teal-dark text-white border-transparent"
                                                : "bg-white border-cream-dark text-[#1F2624] hover:bg-cream"
                                                }`}
                                        >
                                            {r.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Would miss most */}
                            <div>
                                <label className="block">
                                    <p className="text-sm font-medium text-[#1F2624]">
                                        What would you miss the most if Memory Lane disappeared tomorrow?
                                    </p>
                                    <p className="text-xs text-[#4C5551] mt-0.5">One sentence is plenty.</p>
                                    <textarea
                                        value={wouldMiss}
                                        onChange={(e) => setWouldMiss(e.target.value)}
                                        data-testid="side-would-miss"
                                        rows={3}
                                        maxLength={500}
                                        placeholder="Lane was up at 2am when no one else was…"
                                        className="mt-2 w-full px-3 py-2 rounded-xl text-sm border border-cream-dark bg-white text-[#1F2624] focus:border-teal focus:ring-1 focus:ring-teal outline-none resize-none"
                                    />
                                </label>
                            </div>

                            <div className="pt-2 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={submitting}
                                    data-testid="side-survey-submit"
                                    className={`flex-1 inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-medium transition-colors ${submitting
                                        ? "bg-teal-dark/60 text-white cursor-wait"
                                        : "bg-teal-dark hover:bg-teal-dark/90 text-white"
                                        }`}
                                >
                                    {submitting ? "Sending…" : "Submit"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onOpenChange(false)}
                                    data-testid="side-survey-cancel"
                                    className="text-xs text-[#4C5551] hover:text-teal-dark px-3 py-2"
                                >
                                    Not now
                                </button>
                            </div>
                            <p className="text-[11px] text-[#6E7A74] leading-snug text-center">
                                Want the full 8-question survey? <a href="/feedback" className="text-teal-dark hover:underline">Open it on its own page →</a>
                            </p>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
